import useExecuteExportJob from "@babackapp/private-api-client/client/use-execute-export-job";
import useUpdateExportMethod from "@babackapp/private-api-client/client/use-update-export-method";
import type { ShopExportDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { ButtonLoading } from "@babackapp/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@babackapp/ui/components/dialog";
import type { PropsWithChildren } from "react";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useBoolean } from "@/hooks/use-boolean";
import ExportSettingsForm, {
	type ExportSettingsFormValues,
} from "./export-settings-form";

type UpdateExportSettingsDialogProps = {
	shopExport: ShopExportDto;
};

export default function UpdateExportSettingsDialog({
	shopExport,
	children,
}: PropsWithChildren<UpdateExportSettingsDialogProps>) {
	const open = useBoolean();
	const t = useTranslation();
	const { data: shop } = useCurrentShopSuspense();
	const { mutateAsync: updateExport } = useUpdateExportMethod(shop, {
		onSuccess: open.setFalse,
	});
	const { mutateAsync: executeExport, isPending: isExecuting } =
		useExecuteExportJob(shop);

	const onValidate = () => {};

	const onSubmit = ({
		options: { modelId, cron, resolutions, statuses, locationIds, returnStoreIds },
		server: { host, user, password, path, serverType },
	}: ExportSettingsFormValues) => {
		const next = { ...shopExport };
		next.cron = cron;
		next.filter = {
			status: statuses,
			resolution: resolutions,
			locationIds,
			returnStoreIds,
		};
		next.exportModelId = modelId;

		next.type = serverType;

		next.login = {
			host,
			user,
			password,
			path,
		};

		updateExport({
			exportId: shopExport.jobId,
			shopExport: next,
		});
	};

	return (
		<Dialog open={open.value} onOpenChange={open.setValue}>
			<DialogTrigger asChild>{children}</DialogTrigger>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						{t("settings.integrations.exports.updateExport.title")}
					</DialogTitle>
					<DialogDescription>
						{t("settings.integrations.exports.updateExport.description")}
					</DialogDescription>
				</DialogHeader>
				<ButtonLoading
					isLoading={isExecuting}
					variant="outline"
					onClick={() => {
						executeExport(shopExport.jobId);
					}}
				>
					{t("misc.execute")}
				</ButtonLoading>
				<ExportSettingsForm
					onValidate={onValidate}
					onSubmit={onSubmit}
					defaultValues={{
						server: {
							host: shopExport.login?.host,
							user: shopExport.login?.user,
							password: shopExport.login?.password,
							path: shopExport.login?.path,
							serverType: shopExport.type as "ftp" | "sftp",
						},
						options: {
							cron: shopExport.cron,
							modelId: shopExport.exportModelId ?? "",
							resolutions: shopExport.filter?.resolution,
							statuses: shopExport.filter?.status,
							locationIds: shopExport.filter?.locationIds ?? [],
							returnStoreIds: shopExport.filter?.returnStoreIds ?? [],
						},
					}}
				/>
			</DialogContent>
		</Dialog>
	);
}
